import { useState, useEffect, useCallback, useRef } from 'react';
import { logError, logInfo } from '../../../utils/logger';

export const useLocationTracking = () => {
  const [userLocation, setUserLocation] = useState(null);
  const [locationStatus, setLocationStatus] = useState('checking');
  const [isLocked, setIsLocked] = useState(true);
  const [hasSignal, setHasSignal] = useState(false);
  const [locationData, setLocationData] = useState({
    accuracy: null,
    altitude: 0,
    heading: null,
    speed: null
  });

  // Use refs to store timeouts and state
  const timeoutRefs = useRef({
    init: null,
    retry: null,
    watch: null
  });
  const retryCount = useRef(0);
  const mounted = useRef(true);
  const watchId = useRef(null);

  // Constants
  const MAX_RETRIES = 5;
  const RETRY_DELAY = 5000;
  const LOCATION_TIMEOUT = 30000;

  const handleSuccess = useCallback((position) => {
    if (!mounted.current) return;

    const newLocation = {
      lat: position.coords.latitude,
      lng: position.coords.longitude
    };
    
    setUserLocation(newLocation);
    setLocationData({
      accuracy: position.coords.accuracy,
      altitude: position.coords.altitude || 0,
      heading: position.coords.heading,
      speed: position.coords.speed
    });
    setLocationStatus('available');
    setHasSignal(true);
    retryCount.current = 0;
    logInfo('Location updated successfully');
  }, []);

  const handleError = useCallback((error) => {
    if (!mounted.current) return;

    logError('Location error:', error);
    setHasSignal(false);

    switch (error.code) {
      case error.PERMISSION_DENIED:
        setLocationStatus('denied');
        break;
      case error.POSITION_UNAVAILABLE:
        setLocationStatus('unavailable');
        break;
      case error.TIMEOUT:
        setLocationStatus('timeout');
        if (retryCount.current < MAX_RETRIES) {
          retryCount.current++;
          timeoutRefs.current.retry = setTimeout(startTracking, RETRY_DELAY);
          logInfo(`Retrying location tracking (attempt ${retryCount.current}/${MAX_RETRIES})`);
        } else {
          setLocationStatus('error');
          logError('Location tracking failed after max retries');
        }
        break;
      default:
        setLocationStatus('error');
    }
  }, []);

  const startTracking = useCallback(() => {
    if (!navigator.geolocation) {
      setLocationStatus('unavailable');
      return;
    }

    // Clear any existing timeouts
    Object.values(timeoutRefs.current).forEach(timeout => {
      if (timeout) clearTimeout(timeout);
    });

    // Clear existing watch
    if (watchId.current) {
      navigator.geolocation.clearWatch(watchId.current);
    }

    // Get initial position
    navigator.geolocation.getCurrentPosition(
      handleSuccess,
      handleError,
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );

    // Start watching position
    watchId.current = navigator.geolocation.watchPosition(
      handleSuccess,
      handleError,
      {
        enableHighAccuracy: true,
        timeout: LOCATION_TIMEOUT,
        maximumAge: 0
      }
    );

    logInfo('Location tracking started');
  }, [handleSuccess, handleError]);

  // Initialize tracking
  useEffect(() => {
    startTracking();

    return () => {
      mounted.current = false;
      
      // Clear all timeouts
      Object.values(timeoutRefs.current).forEach(timeout => {
        if (timeout) clearTimeout(timeout);
      });

      // Clear watch
      if (watchId.current) {
        navigator.geolocation.clearWatch(watchId.current);
        watchId.current = null;
      }

      logInfo('Location tracking cleanup complete');
    };
  }, [startTracking]);

  const toggleLock = useCallback(() => {
    setIsLocked(prev => !prev);
    logInfo(`Location lock ${!isLocked ? 'enabled' : 'disabled'}`);
  }, [isLocked]);

  return {
    userLocation,
    locationStatus,
    ...locationData,
    isLocked,
    hasSignal,
    toggleLock
  };
};

export default useLocationTracking;